import React from 'react';
import { X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useBeneficiairesStore } from '../../stores/beneficiairesStore';
import { useThemeStore } from '../../stores/themeStore';
import { ATELIER_TYPES } from '../../data/atelierTypes';

interface AtelierFormProps {
  onSubmit: (data: any) => void;
  onClose: () => void;
  atelier?: any;
  isEditing?: boolean;
}

export default function AtelierForm({ onSubmit, onClose, atelier, isEditing = false }: AtelierFormProps) {
  const { beneficiaires } = useBeneficiairesStore();
  const { darkMode } = useThemeStore();
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: isEditing && atelier ? {
      name: atelier.name,
      workshopType: atelier.workshopType || 'cv',
      date: atelier.date,
      startTime: atelier.startTime,
      endTime: atelier.endTime,
      location: atelier.location,
      address: atelier.address,
      description: atelier.description || '',
      maxParticipants: atelier.maxParticipants,
      invitedBeneficiaires: atelier.invitedBeneficiaires || [],
      notes: atelier.notes || ''
    } : {
      workshopType: 'cv',
      invitedBeneficiaires: []
    }
  });

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto`}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {isEditing ? "Modifier l'atelier" : 'Nouvel atelier'}
              </h2>
              <button type="button" onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>Informations générales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Nom de l'atelier *
                    </label>
                    <input
                      type="text"
                      {...register("name", { required: "Le nom est requis" })}
                      className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                      placeholder="Ex : Atelier CV du 15 mars"
                    />
                    {errors.name && <span className="text-sm text-red-600">{errors.name.message}</span>}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                      Type d'atelier *
                    </label>
                    <select
                      {...register("workshopType", { required: "Le type est requis" })}
                      className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                    >
                      {ATELIER_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Date *</label>
                  <input
                    type="date"
                    {...register("date", { required: "La date est requise" })}
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  />
                  {errors.date && <span className="text-sm text-red-600">{errors.date.message}</span>}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Heure de début *</label>
                  <input
                    type="time"
                    {...register("startTime", { required: "L'heure de début est requise" })}
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  />
                  {errors.startTime && <span className="text-sm text-red-600">{errors.startTime.message}</span>}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Heure de fin *</label>
                  <input
                    type="time"
                    {...register("endTime", { required: "L'heure de fin est requise" })}
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  />
                  {errors.endTime && <span className="text-sm text-red-600">{errors.endTime.message}</span>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Lieu *</label>
                  <input
                    type="text"
                    {...register("location", { required: "Le lieu est requis" })}
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                    placeholder="Nom du lieu"
                  />
                  {errors.location && <span className="text-sm text-red-600">{errors.location.message}</span>}
                </div>
                <div>
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Adresse *</label>
                  <input
                    type="text"
                    {...register("address", { required: "L'adresse est requise" })}
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  />
                  {errors.address && <span className="text-sm text-red-600">{errors.address.message}</span>}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Description</label>
                <textarea
                  {...register("description")}
                  rows={4}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  placeholder="Description de l'atelier..."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Nombre maximum de participants</label>
                <input
                  type="number"
                  {...register("maxParticipants", { min: 1 })}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>Candidats invités</label>
                <select
                  multiple
                  {...register("invitedBeneficiaires")}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  size={5}
                >
                  {beneficiaires.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.firstName} {b.lastName} - {b.title}
                    </option>
                  ))}
                </select>
                <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Maintenez Ctrl (Windows) ou Cmd (Mac) pour sélectionner plusieurs candidats
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Notes internes</label>
                <textarea
                  {...register("notes")}
                  rows={3}
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2.5`}
                  placeholder="Notes internes sur l'organisation..."
                />
              </div>
            </div>
          </div>

          <div className={`px-6 py-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-b-xl flex justify-end gap-4`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'} rounded-lg`}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gradient-anthea text-white rounded-lg hover:opacity-90"
            >
              {isEditing ? 'Enregistrer les modifications' : "Créer l'atelier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
